import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Input, Offcanvas, OffcanvasBody, OffcanvasHeader } from 'reactstrap';
import classNames from 'classnames';

import { AuthTags } from '~constants/authTags';

import { getChangedValues } from '~libs/dataUtils';

import { Button, ErrorResponse, Spinner } from '~components';
import { DateInput, Select, Switch } from '~components/form-fields/inputs';
import { Row, TableView, TableViewHandle } from '~components/table-view-v3';

import { InvoiceStatusTransitions } from '~lookups/models';
import { SupplierInvoiceItem, SupplierInvoice as SupplierInvoiceType } from '~support-at-home/models';

import {
  fetchSahWrapsAndItems,
  fetchValidServices,
  supplierInvoiceColumnsFactory,
} from '~support-at-home/forms/supplier-invoices/supplierInvoiceItems';

import { getAll } from '~appointments/actions/serviceTypes';
import { getAll as getAllStatuses, useGetInvoiceStatusTransitionsQuery } from '~lookups/actions/invoiceStatuses';
import { getClientPackageSchedules } from '~main/actions/clients/clientPackageSchedules';
import { getAll as getAllAccountCodes } from '~support-at-home/actions/accountCodes';
import { getBudgetExternalServices } from '~support-at-home/actions/budgetServices';
import { useLazyGetAssocByServiceTypeQuery } from '~support-at-home/actions/sahServiceTypeAssociations';
import { createInvoice, updateInvoice } from '~support-at-home/actions/supplierInvoices';
import { getAll as getAllSuppliers } from '~support-at-home/actions/suppliers';

import { clearClientPackageSchedules } from '~main/reducers/clients/clientPackageSchedules';

import { selectAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';
import { selectInvoiceStatuses } from '~lookups/selectors/invoiceStatuses';
import { selectClientPackageSchedules } from '~main/selectors/clients/clientPackageSchedules';
import { selectLookupClients } from '~main/selectors/clients/clients';
import { selectUserTags } from '~main/selectors/login';
import { selectAccountInvoiceCodes } from '~support-at-home/selectors/accountCodes';
import { selectAllErrors, selectCurrentSupplierInvoice } from '~support-at-home/selectors/supplierInvoices';
import { selectSuppliers } from '~support-at-home/selectors/suppliers';

interface ComponentProps {
  isOpen: boolean;
  isNew: boolean;
  onClose: (confirm: boolean, id?: string) => void;
  className?: string;
  initialValues?: Partial<SupplierInvoiceType>;
  authTag?: string;
}

const SupplierInvoice: React.FC<ComponentProps> = ({ isOpen, onClose, isNew, className }) => {
  const dispatch = useAppDispatch();
  const error = useAppSelector(selectAllErrors);
  const accountCodes = useAppSelector(selectAccountInvoiceCodes);
  const supplierInvoice = useAppSelector(selectCurrentSupplierInvoice);
  const packageSchedules = useAppSelector(selectClientPackageSchedules);
  const serviceTypes = useAppSelector(selectAppointmentServiceTypes);
  const invoiceStatuses = useAppSelector(selectInvoiceStatuses);

  const authTags = AuthTags.SUPPORTATHOME.SUPPLIERINVOICES;
  const authTagUpdate = authTags.UPDATE;

  const tags = useAppSelector(selectUserTags);
  const authorised = tags.includes(authTagUpdate);

  const initialFormData = useMemo(
    (): Partial<SupplierInvoiceType> => ({
      client_id: undefined,
      supplier_id: '',
      status_id: invoiceStatuses.find((status) => status.tag === 'pending')?.id,
      invoice_date: '',
      due_date: '',
      reference: '',
      items: [],
    }),
    [],
  );

  const defaultItemData = useMemo(
    () => ({
      gst_type: 'nin',
    }),
    [],
  );

  const [formData, setFormData] = useState(initialFormData);
  const [originalValues, setOriginalValues] = useState(initialFormData);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isLoading, setLoading] = useState<boolean>(false);
  const [searchByPackage, setSearchByPackage] = useState<boolean>(true);

  const [fetchAssocByServiceType] = useLazyGetAssocByServiceTypeQuery();

  useEffect(() => {
    dispatch(getAll());
    dispatch(getAllSuppliers({ limit: null }));
    dispatch(getAllStatuses());
    dispatch(getAllAccountCodes());
  }, []);

  useEffect(() => {
    const preloadItemMeta = async () => {
      if (!supplierInvoice) return;

      const client_id = supplierInvoice.client_id ? Number(supplierInvoice.client_id) : undefined;
      const itemsWithMeta = await Promise.all(
        (supplierInvoice.items ?? []).map(async (item) => {
          const _meta: any = {};

          let client_budget_id = item.client_budget_id ?? undefined;

          if (client_id && item.service_date) {
            const params: any = {
              client_id,
              date: item.service_date,
            };
            if (searchByPackage && item.client_package_id) {
              params.package_id = item.client_package_id;
            }

            const { payload } = await dispatch(getBudgetExternalServices(params));
            _meta.validServices = payload?.data ?? [];

            const match =
              _meta.validServices.find(
                (valid: any) =>
                  (item.purchase_order_number && valid.purchase_order_number === item.purchase_order_number) ||
                  (item.service_type_id && valid.service_type_id === item.service_type_id),
              ) ?? null;

            client_budget_id = match?.budget?.id ?? null;
          }

          // Fetch full list of available wraps/items based on service_type or PO
          if (item.service_type_id || item.purchase_order_number) {
            const changedKey = item.purchase_order_number ? 'purchase_order_number' : 'service_type_id';
            const assocUpdates = await fetchSahWrapsAndItems({ ...item, _meta }, undefined, changedKey);
            if (assocUpdates?._meta) {
              Object.assign(_meta, assocUpdates._meta);
            }
          }

          return { ...item, client_budget_id, _meta } as SupplierInvoiceItem;
        }),
      );

      const loadedData: Partial<SupplierInvoiceType> = {
        id: supplierInvoice.id,
        client_id,
        supplier_id: supplierInvoice.supplier_id || '',
        status_id: supplierInvoice.status_id || '',
        status: supplierInvoice.status,
        invoice_date: supplierInvoice.invoice_date || '',
        due_date: supplierInvoice.due_date || '',
        reference: supplierInvoice.reference || '',
        items: itemsWithMeta,
      };

      setFormData(loadedData);
      setOriginalValues(loadedData);
      dispatch(getClientPackageSchedules({ client_id: loadedData.client_id, sah: true }));
    };

    if (supplierInvoice) {
      // Determine search mode based on whether ANY item has a PO
      const hasPurchaseOrders = (supplierInvoice.items ?? []).some((item) => item.purchase_order_number);
      if (hasPurchaseOrders) setSearchByPackage(false);

      preloadItemMeta();
    } else if (isNew) {
      setFormData(initialFormData);
      setOriginalValues(initialFormData);
    }
  }, [supplierInvoice, isNew, initialFormData]);

  const getErrorKey = (fieldName: string): string => {
    const errorKeyMap: Record<string, string> = {
      client_id: 'client',
      supplier_id: 'supplier',
      status_id: 'status',
    };
    return errorKeyMap[fieldName] || fieldName;
  };

  const validateField = useCallback((name: string, value: any): string | null => {
    switch (name) {
      case 'client_id':
        return !value ? 'Client is required' : null;
      case 'supplier_id':
        return !value ? 'Supplier is required' : null;
      case 'reference':
        return !value || !value.trim() ? 'Invoice number is required' : null;
      case 'invoice_date':
        return !value ? 'Invoice date is required' : null;
      default:
        return null;
    }
  }, []);

  const onFormChange = useCallback(
    (name: string, value: any) => {
      // Update form data using functional update to avoid dependencies
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Validate the field and update errors accordingly using functional update
      setValidationErrors((prev) => {
        const fieldError = validateField(name, value);
        const errorKey = getErrorKey(name);
        const newErrors = { ...prev };

        if (fieldError) {
          newErrors[errorKey] = fieldError;
        } else {
          delete newErrors[errorKey];
        }

        return newErrors;
      });
    },
    [validateField],
  );

  const { data: allowedStatusTransitions = [] } = useGetInvoiceStatusTransitionsQuery();

  const getStatusTransitionOptions = () => {
    if (isNew) return [{ label: 'Pending', value: initialFormData.status_id as string }];

    const mappedTransitions = allowedStatusTransitions
      .filter((transition: InvoiceStatusTransitions) => transition.from_status_id === originalValues.status_id)
      .map((transition: InvoiceStatusTransitions) => ({
        label: transition.to_status?.name,
        value: transition.to_status_id,
      }))
      .filter((obj, i, self) => self.findIndex((t) => t.value === obj.value) === i);

    return [
      ...mappedTransitions,
      { label: originalValues.status?.name, value: originalValues.status_id } as { label: string; value: string },
    ];
  };

  const renderButtonContent = () => {
    if (isLoading) {
      return <Spinner loading={isLoading} size={4} />;
    }
    return isNew ? 'Create Invoice' : 'Update Invoice';
  };

  const handleClientUpdate = useCallback(
    (client_id: number) => {
      setFormData((prev) => ({
        ...prev,
        items: [],
      }));
      dispatch(clearClientPackageSchedules());

      // Refetch package schedules for new client
      if (client_id) {
        dispatch(getClientPackageSchedules({ client_id, sah: true }));
      }
    },
    [dispatch],
  );

  const handleClientSelect = useCallback(
    (field: string, value: any) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // validate client field
      setValidationErrors((prev) => {
        const fieldError = validateField(field, value);
        const errorKey = getErrorKey(field);
        const newErrors = { ...prev };
        if (fieldError) newErrors[errorKey] = fieldError;
        else delete newErrors[errorKey];
        return newErrors;
      });

      // clear + refetch packages for new client
      handleClientUpdate(value);
    },
    [validateField, handleClientUpdate],
  );

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validate all fields using the same validation logic
    const fieldsToValidate = ['client_id', 'supplier_id', 'reference', 'invoice_date', 'status_id'];

    fieldsToValidate.forEach((fieldName) => {
      const fieldError = validateField(fieldName, (formData as any)[fieldName]);
      if (fieldError) {
        const errorKey = getErrorKey(fieldName);
        errors[errorKey] = fieldError;
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const cleanPayloadForSubmission = useCallback(
    (data: Partial<SupplierInvoiceType>) => {
      let payload: any = {};
      if (isNew) {
        // For new records, filter out empty/initial values
        Object.entries(data).forEach(([key, value]) => {
          // Skip empty strings, undefined values, and empty arrays
          if (value !== '' && value !== undefined && !(Array.isArray(value) && value.length === 0)) {
            // For string fields, also skip whitespace-only values
            if (typeof value === 'string' && !value.trim()) {
              return;
            }
            payload[key] = value;
          }
        });
      } else {
        // For existing records, only send changed values
        payload = getChangedValues(data, originalValues);
      }

      // always retain id for updates
      if (data.id) {
        payload.id = data.id;
      }

      // strip _meta fields, nested objects from items
      const items = (payload.items ?? data.items ?? []).map(
        ({ reference, sah_wrap, sah_item, budget, total, _meta, ...rest }: SupplierInvoiceItem) => ({
          ...defaultItemData,
          ...rest,
        }),
      );

      return { ...payload, items };
    },
    [isNew, getChangedValues, originalValues],
  );

  const handleSubmit = async () => {
    const itemsValid = await tableRef.current?.validate();
    if (!validateForm() || !itemsValid) {
      return;
    }

    setLoading(true);

    const cleanPayload = cleanPayloadForSubmission(formData);

    try {
      let result;
      if (isNew) {
        result = await dispatch(createInvoice(cleanPayload));
      } else {
        result = await dispatch(updateInvoice(cleanPayload));
      }

      if (result.payload?.success) {
        onClose(true, result.payload.data?.id);
      }
    } catch (err) {
      console.error('Error saving invoice:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = useCallback(() => {
    setFormData(initialFormData);
    setOriginalValues(initialFormData);
    setValidationErrors({});
    onClose(false);
  }, [initialFormData, onClose]);

  const tableRef = useRef<TableViewHandle>(null);

  const supplierInvoiceItemsColumns = useMemo(() => {
    return supplierInvoiceColumnsFactory(
      searchByPackage ? 'package' : 'po',
      accountCodes,
      packageSchedules,
      serviceTypes,
    );
  }, [searchByPackage, accountCodes, packageSchedules, serviceTypes]);

  // Returns all rows on any table row change
  const handleDataChange = useCallback(
    (allRows: Row[]) => {
      onFormChange('items', allRows);
    },
    [onFormChange],
  );

  const handleRowEdit = async (row: any, isEditing: boolean) => {
    if (!isEditing) return; // only act on entering edit mode
    if (!formData.client_id || !row.service_date) return;
    if (row._meta && row._meta?.validServices) return;

    const extra = await fetchValidServices(row, {
      client_id: formData.client_id,
      mode: searchByPackage ? 'package' : 'po',
    });

    if (extra && extra._meta) {
      // merge into items to make _meta.validServices available to columnDefinitions
      setFormData((prev) => ({
        ...prev,
        items: prev.items?.map((item) => (item.id === row.id ? { ...item, _meta: extra._meta } : item)) || [],
      }));
    }

    if (row.service_type_id) {
      try {
        const { data: assoc } = await fetchAssocByServiceType({ id: row.service_type_id.toString() });
        if (assoc) {
          setFormData((prev) => ({
            ...prev,
            items:
              prev.items?.map((item) =>
                item.id === row.id
                  ? {
                      ...item,
                      _meta: { ...item._meta, assocItems: assoc.items, assocWraps: assoc.wraps },
                    }
                  : item,
              ) ?? [],
          }));
        }
      } catch (err) {
        console.error('Failed to fetch service associations', err);
      }
    }
  };

  const canvasClassName = classNames('supplier-invoice', [className]);
  const getValidationClassName = (check: string) => (check?.length > 0 ? 'border border-danger rounded' : '');

  return (
    <Offcanvas isOpen={isOpen} size="xl" className={canvasClassName} direction="end">
      <OffcanvasHeader className="header">{isNew ? 'New Supplier Invoice' : 'Edit Supplier Invoice'}</OffcanvasHeader>
      <OffcanvasBody>
        <ErrorResponse message={error} />
        <div className="row d-flex justify-content-between">
          <div className="col top-pill">
            <label>Client</label>
            <Select
              id="client_id"
              name="client_id"
              type="select"
              className={getValidationClassName(validationErrors.client)}
              disabled={!authorised || !isNew || (formData.items?.length ?? 0) > 0}
              selectorOptions={selectLookupClients}
              value={formData.client_id}
              onChange={handleClientSelect}
            />
            {validationErrors.client && <div className="text-danger small">{validationErrors.client}</div>}
          </div>
          <div className="col top-pill">
            <label>Invoice Number</label>
            <Input
              type="text"
              id="reference"
              name="reference"
              className={getValidationClassName(validationErrors.reference)}
              value={formData.reference}
              onChange={(e) => onFormChange(e.target.name, e.target.value)}
              disabled={!authorised}
            />
            {validationErrors.reference && <div className="text-danger small">{validationErrors.reference}</div>}
          </div>
          <div className="col top-pill">
            <label>Supplier</label>
            <Select
              id="supplier_id"
              name="supplier_id"
              type="select"
              className={getValidationClassName(validationErrors.supplier)}
              selectorOptions={selectSuppliers}
              value={formData.supplier_id}
              onChange={onFormChange}
              disabled={!authorised}
            />
            {validationErrors.supplier && <div className="text-danger small">{validationErrors.supplier}</div>}
          </div>
        </div>
        <div className="row d-flex justify-content-between">
          <div className="col top-pill">
            <label>Invoice Date</label>
            <DateInput
              type="date"
              id="invoice_date"
              name="invoice_date"
              className={getValidationClassName(validationErrors.invoice_date)}
              value={formData.invoice_date}
              onChange={onFormChange}
              disabled={!authorised}
            />
            {validationErrors.invoice_date && <div className="text-danger small">{validationErrors.invoice_date}</div>}
          </div>
          <div className="col top-pill">
            <label>Due Date</label>
            <DateInput
              type="date"
              id="due_date"
              name="due_date"
              value={formData.due_date}
              onChange={onFormChange}
              disabled={!authorised}
            />
          </div>
          <div className="col top-pill">
            <label>Status</label>
            <Select
              id="status_id"
              name="status_id"
              type="select"
              className={getValidationClassName(validationErrors.status)}
              options={invoiceStatuses}
              gateOptions={{ getValidOptions: getStatusTransitionOptions }}
              value={formData.status_id}
              onChange={onFormChange}
              disabled={!authorised || isNew}
            />
          </div>
        </div>
        <div className="my-1">
          <Switch
            type="switch"
            id="search-switch"
            name="search-switch"
            value={searchByPackage}
            disabled={!authorised || !isNew || (formData?.items?.length ?? 0) > 0}
            onChange={() => setSearchByPackage(!searchByPackage)}
            labels={['Find by PO Reference', 'Find by Package']}
          />
        </div>
        <TableView
          ref={tableRef}
          id="supplier-invoice-items-table"
          data={formData.items || []}
          columns={supplierInvoiceItemsColumns}
          onChange={handleDataChange}
          onEditRow={handleRowEdit}
          context={{ allRows: formData.items, client_id: formData.client_id, mode: searchByPackage ? 'package' : 'po' }}
          readonly={!authorised}
          compactView
          editonly
          bordered
        />
        <div className="d-flex justify-content-between">
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="success"
            disabled={isLoading || (formData.items?.length ?? 0) === 0}
            authTag={authTagUpdate}
          >
            {renderButtonContent()}
          </Button>
        </div>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default SupplierInvoice;
